import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Send, Smile } from 'lucide-react';
import { reactionEmojis } from '../data/mockData';

export default function LiveChat({ messages, onSend }) {
  const { t } = useLanguage();
  const [message, setMessage] = useState('');
  const [showEmojis, setShowEmojis] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = () => {
    if (!message.trim()) return;
    onSend(message.trim());
    setMessage('');
    setShowEmojis(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    handleSend();
  };

  return (
    <div data-testid="live-chat" className="flex flex-col h-full">
      <div className="px-4 py-3 border-b border-white/5">
        <h3 className="font-semibold text-sm">{t('chat')}</h3>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.length === 0 ? (
          <p className="text-slate-500 text-sm text-center py-8">No messages yet. Say hi!</p>
        ) : (
          messages.map((msg, i) => (
            <div key={i} className="chat-message">
              <div className="flex items-center gap-2 mb-1">
                {msg.user_avatar && <img src={msg.user_avatar} alt="" className="w-5 h-5 rounded-full" />}
                <span className="text-xs font-semibold text-[#FF007F]">{msg.user_name}</span>
                <span className="text-[10px] text-slate-500">
                  {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
              <p className="text-sm text-slate-300 pl-7">{msg.message || msg.content}</p>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {showEmojis && (
        <div className="px-4 py-2 border-t border-white/5">
          <div className="emoji-grid">
            {[...reactionEmojis, '\u{1F60A}', '\u{1F602}', '\u{1F60D}', '\u{1F923}', '\u{1F60E}', '\u{1F970}', '\u{1F618}', '\u{1F914}', '\u{1F440}', '\u{1F4AF}', '\u{1F64C}', '\u{1F60F}', '\u{1F525}', '\u{1F495}', '\u{2728}', '\u{1F389}'].map((e, i) => (
              <button key={i} onClick={() => setMessage(prev => prev + e)}>{e}</button>
            ))}
          </div>
        </div>
      )}

      <form onSubmit={handleFormSubmit} className="p-4 border-t border-white/5 flex items-center gap-2">
        <button type="button" onClick={() => setShowEmojis(!showEmojis)}
          className={`p-2 rounded-full transition-all ${showEmojis ? 'bg-[#FF007F]/20 text-[#FF007F]' : 'bg-white/5 hover:bg-white/10'}`}>
          <Smile className="w-5 h-5" />
        </button>
        <input data-testid="chat-input" type="text" value={message}
          onChange={e => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t('sendMessage')}
          className="flex-1 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-white text-sm placeholder-slate-500 focus:outline-none focus:border-[#FF007F]/30" />
        <button data-testid="chat-send-btn" type="submit" className="p-2 rounded-full gradient-button">
          <Send className="w-4 h-4" />
        </button>
      </form>
    </div>
  );
}
